import { BrowserRouter, Routes, Route, Navigate, Link, useMatch } from "react-router-dom";
import { Button, ThemeProvider, CircularProgress, Box, Alert, CssBaseline, IconButton, Tooltip } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import theme from "./theme";
import LoginPage from "./LoginPage";
import NewReservation from "./NewReservation";
import ReservationConfirm from "./ReservationConfirm";
import Menu from "./AppList";
import SekineJibika from "./SekineJibika";
import SekineJibikaNew from "./SekineJibikaNew";
import SekineJibikaExist from "./SekineJibikaExist";
import HospitalBForm from "./WakoJibika";
import Footer from "./Footer";
import { useClient } from "./ClientContext";
import { useAuth0 } from '@auth0/auth0-react';
import LegalInformation from "./LegalInformation";
import ServiceDescription from "./ServiceDescription";
import PrivacyPolicy from './PrivacyPolicy';
import './App.css';

const AppNavigation = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
  const reservationRoute = useMatch('/reservation/*');
  const reservationList = useMatch('/app-list');
  const reservationConfirm = useMatch('/reservation/confirm');
  const listActive = Boolean(reservationList || reservationConfirm);
  const newActive = Boolean(reservationRoute) && !listActive;

  return (
    <nav className="app-navigation" aria-label="メインナビゲーション">
      {isAuthenticated && <>
        <Link className={`navigation-link${newActive ? ' active' : ''}`} aria-current={newActive ? 'page' : undefined} to="/reservation/sekine-jibika/exist"><AddRoundedIcon /><span>新規予約</span></Link>
        <Link className={`navigation-link${listActive ? ' active' : ''}`} aria-current={listActive ? 'page' : undefined} to="/app-list"><EventNoteRoundedIcon /><span>予約一覧</span></Link>
      </>}
    </nav>
  );
};

const App: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, logout, isLoading } = useAuth0();
  const { session, checkTokenValidity } = useClient();
  const handleLogin = () => { loginWithRedirect(); };
  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };
  const handleSignUp = () => { loginWithRedirect(); };
  const sessionText = session.kind === 'ready' ? 'ログイン中' : session.kind === 'checking' ? 'ログイン状態を確認中…' : session.kind === 'signed-out' ? 'ログアウト中。予約にはログインが必要です。' : session.kind === 'expired' ? 'ログインの有効期限が切れました。再ログインしてください。' : 'ログイン状態を確認できません。接続を確認して再試行してください。';
  const sessionNeedsAttention = session.kind === 'expired' || session.kind === 'unavailable';

  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box className="loading-screen" role="status" aria-label="ログイン状態を確認中">
          <CircularProgress size={32} />
          <span>ログイン状態を確認しています</span>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <a className="skip-link" href="#main-content">本文へ移動</a>
        <div className="app-shell">
          <aside className="app-rail">
            <Link to="/" className="app-brand" aria-label="Agent ホーム">
              <span className="brand-mark" aria-hidden="true"><span /></span>
              <span><strong>Agent</strong><small>通院予約アシスタント</small></span>
            </Link>
            <AppNavigation isAuthenticated={isAuthenticated} />
            <div className="rail-note"><span className="rail-note-line" /><p>通院の準備を、<br />ここから。</p></div>
          </aside>
          <div className="app-workspace">
            <header className="workspace-header">
              <span className="workspace-title">予約ポータル</span>
              <div className="header-account">
                <Tooltip title={sessionText}>
                  <span className={`session-indicator session-indicator-${session.kind}`} role="status" tabIndex={0}>
                    {session.kind === 'ready' ? <CheckCircleOutlineIcon aria-hidden="true" /> : session.kind === 'checking' ? <CircularProgress size={18} color="inherit" aria-hidden="true" /> : sessionNeedsAttention ? <ErrorOutlineIcon aria-hidden="true" /> : <AccountCircleOutlinedIcon aria-hidden="true" />}
                    <span className="visually-hidden">{sessionText}</span>
                  </span>
                </Tooltip>
                {isAuthenticated ? (
                  <Tooltip title="ログアウト"><IconButton size="small" color="inherit" aria-label="ログアウト" onClick={handleLogout}><LogoutIcon fontSize="small" /></IconButton></Tooltip>
                ) : (
                  <Tooltip title="ログイン"><IconButton size="small" color="primary" aria-label="ログイン" onClick={handleLogin}><LoginIcon fontSize="small" /></IconButton></Tooltip>
                )}
              </div>
            </header>
            {sessionNeedsAttention && <div className="session-banner">
              <Alert
                severity="warning"
                action={<>
                  <Button color="inherit" onClick={() => void checkTokenValidity()}>再試行</Button>
                  <Button color="inherit" onClick={handleLogin}>再ログイン</Button>
                </>}
              >
                {sessionText}
              </Alert>
            </div>}
            <main id="main-content" className="workspace-content" tabIndex={-1}>
              <Box component="fieldset" disabled={isAuthenticated && session.kind !== 'ready'} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/reservation/sekine-jibika/exist" replace />
              ) : (
                <LoginPage handleSignUp={handleSignUp} />
              )
            }
          />
          <Route
            path="/app-list"
            element={isAuthenticated ? <ReservationConfirm /> : <Navigate to="/" />}
          />
          <Route
            path="/reservation/menu"
            element={isAuthenticated ? <Menu /> : <Navigate to="/" />}
          />
          <Route
            path="/reservation/new"
            element={isAuthenticated ? <NewReservation /> : <Navigate to="/" />}
          />
          <Route
            path="/reservation/confirm"
            element={
              isAuthenticated ? <ReservationConfirm /> : <Navigate to="/" />
            }
          />
          <Route
            path="/reservation/sekine-jibika"
            element={isAuthenticated ? <SekineJibika /> : <Navigate to="/" />}
          />
          <Route
            path="/reservation/sekine-jibika/new"
            element={isAuthenticated ? <SekineJibikaNew /> : <Navigate to="/" />}
          />
          <Route
            path="/reservation/sekine-jibika/exist"
            element={isAuthenticated ? <SekineJibikaExist /> : <Navigate to="/" />}
          />
          <Route
            path="/reservation/wako-jibika"
            element={isAuthenticated ? <HospitalBForm /> : <Navigate to="/" />}
          />
          <Route
            path="/legal-information"
            element={<LegalInformation />}
          />
          <Route
            path="/service-description"
            element={<ServiceDescription />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        </Routes>
              </Box>
            </main>
            <Footer />
          </div>
        </div>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
